import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { tradesService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import { transformPositionToTradeItem, type TradeItem, type TradeListParams } from '@/types';

const PAGE_SIZE = 100;

/**
 * LR-546: windowed blotter body. Replaces the count->whole-book two-step
 * (useTradesCount + useTradesAll): the server filters/searches/sorts and we pull
 * fixed-size pages on demand. `trades` is the flattened, transformed loaded
 * window; `totalCount` is the server's total for the current filter.
 *
 * The LR-532 near-real-time poll is preserved — on an infinite query
 * refetchInterval/refetchOnWindowFocus refresh the ALREADY-LOADED pages, which
 * is exactly the "keep the visible window fresh" behaviour we want (not a
 * whole-book re-pull).
 */
export const useTradesInfinite = (
  params?: Omit<TradeListParams, 'limit' | 'offset'>,
  enabled: boolean = true,
) => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.trades.infinite(params),
    // The page param is a row cursor, not a page index: a short page would make
    // `index * PAGE_SIZE` skip rows the server never handed over (LR-572 dp #1).
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      tradesService.getTradesList({ ...params, limit: PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      // An empty page ends the paging whatever `count` says: the total is read per
      // request and can drift under a concurrent write, and trusting it alone would
      // leave `hasNextPage` true while every further fetch returns nothing — one
      // scroll to the bottom of the blotter then loops (LR-572 dp #1).
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    // A new sort / search / tab keeps the previous rows (`isPlaceholderData`) until
    // its first page lands, instead of dropping back to the cold-load skeleton. Not
    // for a disabled query: it would never land, and the rows would read as loading.
    placeholderData: enabled ? keepPreviousData : undefined,
    enabled,
  });

  const trades = useMemo<TradeItem[]>(
    () => (query.data?.pages ?? []).flatMap((p) => p.results).map(transformPositionToTradeItem),
    [query.data],
  );
  // undefined (NOT 0) while the query is pending — collapsing to 0 makes the search
  // bar read "0 found" for the whole in-flight round-trip and on error (kvi-r2 #5).
  // Under placeholder data it is the PREVIOUS selection's total.
  const totalCount = query.data?.pages[0]?.count;

  return { ...query, trades, totalCount };
};
